import type { ExtractionFieldLimits, VerificationQueueItem } from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { refusalCodeOf } from './base-msa-shared';

/**
 * The pure half of the Admin **Extracted fields** surface and of the queue's
 * admin-field hints — CLM-109. Kept apart from `ExtractionFields.tsx` for the
 * `field-accuracy-rows.ts` reason: a component module that also exports
 * functions breaks fast refresh, and the copy is shared by the page, the queue
 * and the verify panel.
 *
 * Role lists are *display hints* (`mayAttempt`, `permissions.ts`): the API's
 * action check is the gate.
 */

/** The roles the gateway grants `admin:configure` to — who may read and change the set (R-109-14). */
export const FIELD_SET_ADMIN_ROLES: readonly string[] = ['admin'];

/** The prefix every admin-defined field's name carries (R-109-2). */
export const ADMIN_FIELD_PREFIX = 'additional_terms.';

/** The 403 copy: both endpoints are `admin:configure`, the admin role's alone. */
export const EXTRACTION_FIELDS_FORBIDDEN_COPY =
  'The extracted-field set is configured by administrators (admin:configure). Legal and contributors do not hold ' +
  'that action; the verification queue shows every field, admin-defined ones included, as before.';

/** The deactivate confirmation, verbatim from the spec (§4.7). */
export const DEACTIVATE_CONFIRM_COPY = 'Stops extraction from the next contract. Existing values and queue rows are kept.';

/** The cost of adding a field, said on the form rather than discovered in the queue (R-109-13). */
export const ADD_COST_COPY =
  'Each active field adds one row to the verification queue for every contract extracted after it is saved. ' +
  'Contracts already extracted are not re-extracted.';

/** Under the governed list: a governed change is code, a snapshot and a version bump. */
export const GOVERNED_COPY =
  'These are extracted for every contract and change only with a release — a code change, a schema snapshot and a ' +
  'SCHEMA_VERSION bump. An admin field may not reuse one of their names.';

/* -------------------------------------------------------------------------- */
/* Refusals — the page's own words, with the cap                              */
/* -------------------------------------------------------------------------- */

/** A refusal the page explains in its own words. `up` for a 409 (nothing failed), `bad` for a 400 or 404. */
export interface FieldSetRefusalCopy {
  readonly code: string;
  readonly tone: 'up' | 'bad';
  readonly head: string;
  readonly body: string;
}

/** A failed change as the page holds it. `refusal: null` is everything else, which `ErrorNotice` renders by kind. */
export interface FieldSetProblem {
  readonly kind: ApiProblemKind;
  readonly refusal: FieldSetRefusalCopy | null;
  readonly correlation_id: string | null;
}

type CopyFor = (limits: ExtractionFieldLimits) => { readonly head: string; readonly body: string };

/** 400 — the change cannot be saved as asked. Each body names the cap. */
const VALIDATION_COPY: Readonly<Record<string, CopyFor>> = Object.freeze({
  top_level_refused: (limits) => ({
    head: 'Admin fields are list entries, never top-level properties',
    body:
      `The extraction request already uses ${String(limits.union_count)} of the ${String(limits.union_limit)} union-typed ` +
      'parameters Anthropic allows, and a new top-level property would be one more — the rejection CLM-80 fixed. Nothing was saved.',
  }),
  invalid_field_key: () => ({
    head: 'That key cannot be used',
    body: 'A key is one lower-case snake_case word of at most 48 characters, starting with a letter — for example service_credit_cap.',
  }),
  duplicates_governed_field: () => ({
    head: 'That key is the name of a governed field',
    body: 'Governed fields are extracted already and change only with a release; an admin field of the same name would extract one fact twice. Choose another key.',
  }),
  description_bounds: (limits) => ({
    head: 'The description is outside its bounds',
    body: `A description is one line of ${String(limits.description_min)}–${String(limits.description_max)} characters, with no line breaks.`,
  }),
  description_requests_document_text: () => ({
    head: 'The description asks for document text',
    body:
      'An admin field holds a short value — a number, a date, a condensed term. A description asking the model to quote, copy or ' +
      'transcribe a clause is refused (CLAUDE.md rule 13): the text is already stored, and output is priced several times input.',
  }),
  description_names_party: () => ({
    head: 'The description names a known party',
    body:
      'Descriptions are sent to the model with every contract and are not pseudonymised, so a party name in one would reach the ' +
      'model in clear (FR-29). Describe the fact without the name.',
  }),
  description_requests_personal_data: () => ({
    head: 'The description asks for an individual’s data',
    body:
      'Admin fields capture commercial terms, not people: signatory names, email addresses and phone numbers are refused (CR-03). ' +
      'Whether that should ever change is a decision for Legal.',
  }),
  /* The key is sent to the model in clear too, as the name of the value it returns, so the same three screens read it. */
  field_key_requests_document_text: () => ({
    head: 'The key asks for document text',
    body:
      'The key is sent to the model with every contract, as the name of the value it returns, so a key such as ' +
      'verbatim_indemnity_clause asks for a clause just as a description would (CLAUDE.md rule 13). Name a short value instead.',
  }),
  field_key_names_party: () => ({
    head: 'The key names a known party',
    body:
      'The key is sent to the model with every contract and is not pseudonymised, so a party name in it would reach the model in ' +
      'clear (FR-29). Choose a key without the name; a key cannot be renamed, so for an existing field, deactivate it and add a new one.',
  }),
  field_key_requests_personal_data: () => ({
    head: 'The key names an individual’s data',
    body:
      'The key is sent to the model with every contract, as the name of the value it returns, so a key such as signatory_email ' +
      'asks for personal data just as a description would (CR-03). Admin fields capture commercial terms, not people.',
  }),
  value_bound_exceeded: (limits) => ({
    head: 'The value bound is too large',
    body: `A value bound is at most ${String(limits.value_max)} characters.`,
  }),
  field_cap_reached: (limits) => ({
    head: 'The admin-field cap is reached',
    body: `At most ${String(limits.active_max)} admin fields may be active at once. Deactivate one before adding or reactivating another.`,
  }),
  output_format_over_budget: (limits) => ({
    head: 'The output format would exceed its budget',
    body:
      `The change would take the extraction request past ${String(limits.union_ceiling)} of ${String(limits.union_limit)} union-typed ` +
      `parameters, give it an optional parameter (it has none by design), or take it past ${String(limits.schema_bytes_max)} bytes. ` +
      'Admin fields cannot do this on their own, so this is a defect to report.',
  }),
  no_change: () => ({ head: 'Nothing to save', body: 'The description and the value bound are already these.' }),
});

/** 409 — the registry's state does not admit the change, or it moved. */
const CONFLICT_COPY: Readonly<Record<string, { readonly head: string; readonly body: string }>> = Object.freeze({
  field_exists: { head: 'A field with this key already exists', body: 'Edit it instead of adding it again.' },
  field_retired: {
    head: 'That field is retired',
    body: 'Reactivate it first. A key is added once, so its accuracy history never mixes two meanings.',
  },
  field_already_active: { head: 'That field is already active', body: 'Nothing was recorded.' },
  field_already_inactive: { head: 'That field is already retired', body: 'Nothing was recorded.' },
  field_set_changed: {
    head: 'The field set changed since this page loaded',
    body: 'Another change was saved first, so nothing was recorded. The list has been reloaded — check it, then decide again.',
  },
});

const NOT_FOUND_COPY = { head: 'That field does not exist', body: 'No admin field has this key; it may never have been added.' };

/**
 * A failed change as the page shows it. A code is trusted only with the status
 * the API sends it under, so a stray field error cannot borrow its words. A
 * 404 from this endpoint can only mean the key: its field errors are not
 * carried (`rejections.ts`), so the status is the code.
 */
export function fieldSetProblemOf(error: unknown, limits: ExtractionFieldLimits): FieldSetProblem {
  const kind = classifyApiError(error);
  const code = refusalCodeOf(error);
  const correlation_id = error instanceof ApiError ? error.correlation_id : null;

  if (kind === 'validation' && code !== null) {
    const copy = VALIDATION_COPY[code];
    if (copy !== undefined) return { kind, correlation_id, refusal: { code, tone: 'bad', ...copy(limits) } };
  }
  if (kind === 'conflict' && code !== null) {
    const copy = CONFLICT_COPY[code];
    if (copy !== undefined) return { kind, correlation_id, refusal: { code, tone: 'up', ...copy } };
  }
  if (kind === 'not_found') return { kind, correlation_id, refusal: { code: 'field_not_found', tone: 'bad', ...NOT_FOUND_COPY } };
  return { kind, correlation_id, refusal: null };
}

/* -------------------------------------------------------------------------- */
/* The queue's hints (R-109-12, R-109-13, R-109-15)                           */
/* -------------------------------------------------------------------------- */

export function isAdminFieldName(fieldName: string): boolean {
  return fieldName.startsWith(ADMIN_FIELD_PREFIX);
}

/**
 * CLM-85's `tables_missing` caveat, extended from `key_terms.*` to
 * `additional_terms.*`: an admin field is a short commercial term of the same
 * kind, and a flattened rate card is as likely to have fed it.
 */
export function tablesMissingApplies(item: Pick<VerificationQueueItem, 'conversion_caveat' | 'field_name'>): boolean {
  return (
    item.conversion_caveat === 'tables_missing' &&
    (item.field_name.startsWith('key_terms.') || item.field_name.startsWith(ADMIN_FIELD_PREFIX))
  );
}

/**
 * Why an admin field's value is empty when the model did return one: over its
 * bound, contained. Without it the empty value reads as "not stated", and
 * settling it Verified would record a silence the document does not have.
 */
export function overBoundCopy(length: number): string {
  return (
    `The model returned ${String(length)} characters for this field, over its bound, so the value was not kept. ` +
    'The empty value does not mean the document is silent — check the PDF, and correct the value rather than confirming it.'
  );
}

/** The description line the queue and the panel show under an admin field's name. */
export function askedWithCopy(description: string): string {
  return `Asked for: ${description}`;
}

/** The retired badge's explanation. */
export const RETIRED_COPY = 'Retired — no longer extracted. This row can still be settled, and it still counts.';
